import { ButtonLink, RiSearchLine, Stack, TextInput } from 'component-library';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Organization } from '@/api/rest/resources/types/organization';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { useOrganization } from '@/hooks/useOrganization';
import { SignedInPageLayout } from '@/layout/signed-in-page';
import { useSetOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { paths } from '@/routing';
import { buildPath } from '@/utils/buildPath';

const OrganizationCard = ({ organization }: { organization: Organization }) => {
  const pathname = useLocation();

  const setOrganizationId = useSetOrganizationId();
  return (
    <ButtonLink
      to={buildPath(paths.staff.plots, { queryParams: { organizationId: organization.id } })}
      state={{
        previousPath: pathname,
      }}
      onClick={() => setOrganizationId(organization.id)}
      variant='outline'
      className='flex w-72 flex-col justify-start items-start gap-2 rounded-xl border border-neutral-black-12 text-text-primary px-8 py-12 hover:bg-neutral-black-4 overflow-hidden'
    >
      <div className='w-10 h-10 bg-neutral-black-4 items-center justify-center flex'>
        <Icon icon='ri-hotel-line' className='text-xl' />
      </div>
      <span className='whitespace-pre-wrap typography-h4'>{organization.name}</span>
    </ButtonLink>
  );
};

export const OrganizationsPage = () => {
  const { t } = useTranslation();

  const [search, setSearch] = useState('');

  const { data } = useOrganization();

  const filtered = useMemo(() => {
    const organizations = data?.results ?? [];
    if (!search) return organizations;
    const s = search.toLowerCase();
    return organizations.filter((o) => o.name.toLowerCase().includes(s));
  }, [data?.results, search]);

  return (
    <SignedInPageLayout
      items={[]}
      withCustomContentContainer
      navBarProps={{
        title: t('global.organization.organizations'),
        showSearchBar: true,
        customSearchBar: <SearchBar value={search} onChange={setSearch} />,
        showFilters: false,
        children: (
          <ButtonLink
            to={buildPath(paths.staff.globalCopilot)}
            variant='outline'
            leftAdornment={<Icon icon='ri-record-circle-line' />}
          >
            {t('global.copilot.title')}
          </ButtonLink>
        ),
      }}
    >
      <Stack className='px-48 py-12'>
        <span className='typography-overline text-text-secondary py-6'>{`${filtered.length} ${t('global.organization.organizations')}`}</span>
        <Stack className='gap-4 flex-wrap flex' direction='row'>
          {filtered.map((org) => (
            <OrganizationCard key={org.id} organization={org} />
          ))}
        </Stack>
      </Stack>
    </SignedInPageLayout>
  );
};

const SearchBar = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
  const { t } = useTranslation();

  return (
    <TextInput
      data-testid='navbar-search-input'
      placeholder={t('shared.searchBar.placeholder')}
      variant='standard'
      value={value}
      className='flex-1 typography-body1'
      StartIcon={<RiSearchLine className='text-primary-100' />}
      size='small'
      onChange={(e) => onChange(e.target.value)}
      InputProps={{
        disableUnderline: true,
        style: {
          borderRadius: '999px',
          backgroundColor: '#004C3D14',
          padding: '4px 16px',
        },
        sx: {
          '& input': {
            paddingTop: '4px',
          },
        },
      }}
    />
  );
};
